import React, {useEffect, useRef, useState} from 'react';
import {FlowData, ProductInstance, RegisterFunction} from '../../../../util/types.ts';
import {ScrollToAnchorPoint} from "../../../../util/ScrollToAnchor.tsx";
import { TechValues } from '../../../../util/SelfcareErrorReportTypes.ts';
import {filterIllegalCharcters, scrollToElement} from "../../../../util/helpers.ts";

interface OwnRouterProps {
    product: ProductInstance;
    flowData: FlowData;
    setFlowData: React.Dispatch<React.SetStateAction<FlowData>>;
    register: RegisterFunction;
}

export default function OwnRouterComponent({
        product,
        flowData,
        setFlowData,
        register,
    }: OwnRouterProps) {

    const fiberRouterLightOptions = [
        {value: '', label: 'Vælg', disabled: true},
        {value: 'Fast/blinker grønt', label: 'Fast/blinker grønt', disabled: false},
        {value: 'Fast rød', label: 'Fast rød', disabled: false},
        {value: 'Intet lys', label: 'Intet lys', disabled: false},
    ];

    const coaxRouterLightOptions = [
        {value: '', label: 'Vælg', disabled: true},
        {value: 'Fast i de 4 nederste lamper', label: 'Fast i de 4 nederste lamper', disabled: false},
        {value: 'Blinker i pil op', label: 'Blinker i pil op', disabled: false},
        {value: 'Blinker i pil ned', label: 'Blinker i pil ned', disabled: false},
        {value: 'Blinker i globus', label: 'Blinker i globus', disabled: false},
        {value: 'Min router er i bridgemode', label: 'Min router er i bridgemode', disabled: false},
    ];

    const tech = product.product.att_statistics_technology;

    const [routerError, setRouterError] = useState<string>('');
    const [routerLightsError, setRouterLightsError] = useState<string>('');

    const ownRouterComponentComponentTop = useRef() as React.MutableRefObject<HTMLDivElement>;
    const ownRouterLightsSectionComponentTop = useRef() as React.MutableRefObject<HTMLDivElement>;

    useEffect((): void => {
        register("OwnRouterComponent",
            {
                hasErrors: (flowData: FlowData): boolean => {
                    if (flowData.ownRouter === null) {
                        setRouterError("Vælg venligst om du bruger Hipers router eller din egen.");
                        return true
                    }

                    if (!flowData.ownRouter && !flowData.ownRouterLights?.trim()) {
                        setRouterLightsError("Beskriv hvordan routeren lyser.");
                        return true
                    }
                    return false
                },
                scrollToError: (): void => {
                    if (flowData.ownRouter === null) {
                        scrollToElement(ownRouterComponentComponentTop.current)
                        return
                    }

                    if (!flowData.ownRouter && !flowData.ownRouterLights?.trim()) {
                        scrollToElement(ownRouterLightsSectionComponentTop.current)
                        return
                    }

                },
            },
        )
    }, [register, routerLightsError, flowData.ownRouter, flowData.ownRouterLights]);

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value === 'yes';
        setFlowData((prev) => ({...prev, ownRouter: value}));

        if (value) {
            setRouterError('');
        }
    };

    const handleTextFieldChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        let input = event.target.value;

        if (input.length > 500) {
            input = input.substring(0, 500);
        }

        const sanitizedInput = filterIllegalCharcters(input);

        setFlowData((prev) => ({ ...prev, ownRouterLights: sanitizedInput }));
        setRouterLightsError('');
    };

    const hasError = flowData.ownRouter === null && routerError.length > 0;
    const hasLightsError = routerLightsError.length > 0;

    return (
        <>
            {tech === TechValues.Fiber && (
                <>
                    <div className={`input-container${hasError ? ' error' : ''}`} ref={ownRouterComponentComponentTop}>
                        <ScrollToAnchorPoint id={'own-router-section'} className="id-anchor--selfcare"/>
                        <p>
                            Bruger du din Hiper-router?*
                        </p>
                        <p className={'with-checkbox'}>
                            <input
                                type={'radio'}
                                id={'own_router_yes'}
                                name={'own_router'}
                                value={'yes'}
                                onChange={handleRadioChange}
                                checked={flowData.ownRouter === true}
                            />
                            <label
                                className={'checkbox-label checkbox-label--montserrat'}
                                htmlFor={'own_router_yes'}
                            >
                                Ja
                            </label>
                        </p>
                        <p className={'with-checkbox'}>
                            <input
                                type={'radio'}
                                id={'own_router_no'}
                                name={'own_router'}
                                value={'no'}
                                onChange={handleRadioChange}
                                checked={flowData.ownRouter === false}
                            />
                            <label
                                className={'checkbox-label checkbox-label--montserrat'}
                                htmlFor={'own_router_no'}
                            >
                                Nej
                            </label>
                        </p>

                        {hasError && <p className={'error-message'}>{routerError}</p>}

                        {flowData.ownRouter === false && (
                            <div className={'text-box'}>
                                <p>
                                    <strong>Obs.:</strong>{' '}
                                    For at fortsætte fejlmeldingen skal du sætte din Hiper-router til.
                                    Vi kan nemlig kun fejlsøge på din Hiper-router.
                                </p>
                                <p>
                                    Hvis du fortsætter fejlmeldingen uden at sætte din Hiper-router til, beder vi dig om at gøre det som det første, når vi kontakter dig for at følge op på fejlmeldingen.
                                </p>
                                <p>
                                    Hvis du har sat din Hiper-router til nu, så vælg &ldquo;Ja&rdquo;.
                                </p>
                            </div>
                        )}
                    </div>

                    {flowData.ownRouter === true && (
                        <div className={`input-container${hasLightsError ? ' error' : ''}`} ref={ownRouterLightsSectionComponentTop}>
                            <ScrollToAnchorPoint id={'own-router-lights-section'} className={'id-anchor--selfcare-report-error'}/>
                            <p>
                                Hvordan lyser din Hiper-router i <strong>globus/internet</strong>-lampen?*
                            </p>
                            <div className={'select-container'}>
                                <select
                                    id={'router_light'}
                                    name={'router_light'}
                                    value={flowData.ownRouterLights}
                                    onChange={(e) => {
                                        setFlowData((prev) => ({ ...prev, ownRouterLights: e.target.value }));
                                        setRouterLightsError('');
                                    }}
                                >
                                    {fiberRouterLightOptions.map((option) => (
                                        <option key={option.value} value={option.value} disabled={option.disabled}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {hasLightsError && <p className={'error-message'}>{routerLightsError}</p>}
                        </div>
                    )}
                </>
            )}

            {tech === TechValues.Coax && (
                <div className={`input-container${hasLightsError ? ' error' : ''}`} ref={ownRouterLightsSectionComponentTop}>
                    <ScrollToAnchorPoint id={'own-router-lights-section'} className={'id-anchor--selfcare-report-error'}/>
                    <p>
                        Hvordan lyser lamperne på din Hiper-router?*
                    </p>
                    <div className={'select-container'}>
                        <select
                            id={'router_light'}
                            name={'router_light'}
                            value={flowData.ownRouterLights}
                            onChange={(e) => setFlowData((prev) => ({...prev, ownRouterLights: e.target.value}))}
                        >
                            {coaxRouterLightOptions.map((option) => (
                                <option key={option.value} value={option.value} disabled={option.disabled}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    {hasLightsError && <p className={'error-message'}>{routerLightsError}</p>}
                </div>
            )}

            {tech !== TechValues.Fiber && tech !== TechValues.Coax && (
                <div className={`input-container${hasLightsError ? ' error' : ''}`} ref={ownRouterLightsSectionComponentTop}>
                    <ScrollToAnchorPoint id={'own-router-lights-section'} className={'id-anchor--selfcare-report-error'}/>
                    <p>
                        Hvordan lyser lamperne på din router?*
                    </p>
                    <textarea
                        id='router_light_description'
                        name='router_light_description'
                        value={flowData.ownRouterLights}
                        onChange={handleTextFieldChange}
                        maxLength={500}
                        rows={4}
                        placeholder={'Eks. "Internet lampen lyser rødt"'}
                    />
                    {hasLightsError && <p className={'error-message'}>{routerLightsError}</p>}

                </div>
            )}
        </>
    );
}
